import { useEffect, useRef, useState } from 'react';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CampaignIcon from '@mui/icons-material/Campaign';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Box, Chip, IconButton, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { FULFILLMENT_ROLE_REVIEWER } from 'common/constants/notificationEventTypes';
import { NOTIFICATION_TYPE } from 'context/NotificationCenterContext';
import type { AppNotification, JobAppNotification } from 'context/NotificationCenterContext';
import {
  GENERAL_NOTIFICATION_THEME,
  getFulfillmentTheme,
  getGlobalEventTheme,
  getNotificationCompanyBodySuffix,
  getNotificationProcessingTypeLabel,
  getNotificationTitle,
  NOTIFICATION_COPY,
  NOTIFICATION_TOAST_DURATION_MS,
} from './NotificationCenter.constants';
import {
  cardTitleSx,
  descriptionSx,
  processingTypeChipSx,
  startedTypeChipSx,
  tagsRowSx,
  timestampSx,
  viewResultsLinkSx,
} from './NotificationCenter.styles';
import { NOTIFICATION_VARIANT } from './NotificationCenter.types';
import { getNotificationUrl, getNotificationVariant, isValidUrl } from './NotificationCenter.utils';
import {
  getNotificationIcon,
  getNotificationIconCircleSx,
  getNotificationTitleColor,
  getToastOuterSx,
} from './NotificationCenter.variantUi';

interface NotificationToastProps {
  notification: AppNotification;
  onDismiss: (id: string) => void;
}

const toastProcessingSx = {
  position: 'relative',
  borderRadius: '14px',
  p: '2px',
  background: 'linear-gradient(to right, #d946ef, #3b82f6, #34d399, #3b82f6, #d946ef)',
  backgroundSize: '200% 100%',
  animation: 'gradientFlow 3s linear infinite',
  '@keyframes gradientFlow': {
    '0%':   { backgroundPosition: '0% 50%' },
    '100%': { backgroundPosition: '200% 50%' },
  },
};

const toastInnerSx = {
  position: 'relative',
  zIndex: 1,
  background: '#fff',
  borderRadius: '12px',
  p: 1.5,
};

const NotificationToast = ({ notification, onDismiss }: NotificationToastProps) => {
  const { id } = notification;
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const variant   = getNotificationVariant(notification);
  const resultUrl = getNotificationUrl(notification);

  const handleClose = () => {
    setExiting(true);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setTimeout(() => onDismiss(id), 280);
  };

  useEffect(() => {
    timerRef.current = setTimeout(() => handleClose(), NOTIFICATION_TOAST_DURATION_MS);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const slideAnimation = {
    animation: exiting
      ? 'toastSlideOut 0.28s cubic-bezier(0.4,0,1,1) forwards'
      : 'toastSlideIn 0.32s cubic-bezier(0,0,0.2,1) forwards',
    '@keyframes toastSlideIn': {
      from: { transform: 'translateX(110%)', opacity: 0 },
      to:   { transform: 'translateX(0)',    opacity: 1 },
    },
    '@keyframes toastSlideOut': {
      from: { transform: 'translateX(0)',    opacity: 1 },
      to:   { transform: 'translateX(110%)', opacity: 0 },
    },
  };

  if (notification.type === NOTIFICATION_TYPE.FULFILLMENT) {
    const theme = getFulfillmentTheme(notification.role);
    const roleLabel = notification.role === FULFILLMENT_ROLE_REVIEWER ? 'Reviewer' : 'Preparer';
    return (
      <Box
        role="presentation"
        sx={{
          background: theme.bg,
          border: `1px solid ${theme.border}`,
          borderRadius: '14px',
          p: 1.5,
          width: 360,
          boxShadow: '0px 8px 32px rgba(0,0,0,0.13)',
          ...slideAnimation,
          cursor: 'default',
        }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 1, alignItems: 'flex-start' }}>
          <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: theme.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <AssignmentIndIcon sx={{ fontSize: 16, color: theme.iconColor }} />
          </Box>
          <Box>
            <Typography sx={{ ...cardTitleSx, color: theme.titleColor }}>Assigned as {roleLabel}</Typography>
            {notification.companyName && (
              <Typography sx={{ fontSize: '0.83rem', color: '#374151', mt: 0.5, lineHeight: 1.4 }}>{notification.companyName}</Typography>
            )}
          </Box>
          <IconButton type="button" size="small" onClick={e => {
            e.preventDefault(); e.stopPropagation(); handleClose(); 
          }} sx={{ p: 0.25, mt: 0.125 }}>
            <CloseIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          </IconButton>
        </Box>
      </Box>
    );
  }

  if (notification.type === NOTIFICATION_TYPE.GLOBAL || notification.type === NOTIFICATION_TYPE.GENERAL) {
    const theme = notification.type === NOTIFICATION_TYPE.GLOBAL
      ? getGlobalEventTheme(notification.eventType)
      : GENERAL_NOTIFICATION_THEME;
    const AnnouncementIcon = notification.type === NOTIFICATION_TYPE.GLOBAL ? CampaignIcon : NotificationsIcon;
    const linkUrl = notification.link && isValidUrl(notification.link) ? notification.link : null;
    const linkLabel = linkUrl ? (notification.linkLabel ?? 'View') : null;
    return (
      <Box
        role="presentation"
        sx={{
          background: theme.bg,
          border: `1px solid ${theme.border}`,
          borderRadius: '14px',
          p: 1.5,
          width: 360,
          boxShadow: '0px 8px 32px rgba(0,0,0,0.13)',
          ...slideAnimation,
          cursor: 'default',
        }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 1, alignItems: 'flex-start' }}>
          <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: theme.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <AnnouncementIcon sx={{ fontSize: 16, color: theme.iconColor }} />
          </Box>
          <Box>
            <Typography sx={{ ...cardTitleSx, color: theme.titleColor }}>{notification.title}</Typography>
            <Typography sx={{ fontSize: '0.83rem', color: '#374151', mt: 0.5, lineHeight: 1.4 }}>{notification.content}</Typography>
            {linkUrl && (
              <Box sx={{ mt: 0.75 }}>
                <Box
                  component="a"
                  href={linkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  sx={{
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    color: theme.titleColor,
                    border: `1px solid ${theme.border}`,
                    borderRadius: '6px',
                    px: 1.25,
                    py: 0.375,
                    textDecoration: 'none',
                    display: 'inline-block',
                    lineHeight: 1.6,
                    '&:hover': { opacity: 0.7 },
                  }}>
                  {linkLabel}
                </Box>
              </Box>
            )}
          </Box>
          <IconButton type="button" size="small" onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            handleClose();
          }} sx={{ p: 0.25, mt: 0.125 }}>
            <CloseIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          </IconButton>
        </Box>
      </Box>
    );
  }

  // After global/general early return, notification is narrowed to JobAppNotification
  const { job } = notification as JobAppNotification;

  const { STARTED, PROCESSING, COMPLETED, FAILED } = NOTIFICATION_VARIANT;

  const outerSx = getToastOuterSx(variant);
  const iconCircle = getNotificationIconCircleSx(variant);
  const icon = getNotificationIcon(variant);
  const title = getNotificationTitle(variant);
  const titleColor = getNotificationTitleColor(variant);

  const inner = (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 1, alignItems: 'flex-start' }}>

      <Box sx={iconCircle}>{icon}</Box>

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ ...cardTitleSx, color: titleColor }}>{title}</Typography>
          {(variant === COMPLETED || variant === FAILED) && (
            <Typography sx={timestampSx}>{NOTIFICATION_COPY.time.justNow}</Typography>
          )}
        </Box>

        {(variant === STARTED || variant === PROCESSING) && (
          <>
            {job.companyName && (
              <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: 'text.secondary', mt: 0.5, lineHeight: 1.2 }}>
                {job.companyName}
              </Typography>
            )}
            {job.processingType && (
              <Box sx={{ mt: 0.75 }}>
                <Chip
                  label={getNotificationProcessingTypeLabel(job.processingType)}
                  size="small"
                  sx={variant === STARTED ? startedTypeChipSx : processingTypeChipSx}
                />
              </Box>
            )}
          </>
        )}

        {(variant === COMPLETED || variant === FAILED) && job.companyName && (
          <Typography sx={descriptionSx} mt={0.5}>
            {job.companyName}{' '}
            {getNotificationCompanyBodySuffix(variant)}
          </Typography>
        )}

        {variant === FAILED && job.errorMessage && (
          <Typography sx={{ ...descriptionSx, color: '#b91c1c', fontSize: '0.78rem' }} mt={0.25}>
            {job.errorMessage}
          </Typography>
        )}

        {(variant === COMPLETED || variant === FAILED) && (
          <>
            {job.processingType && (
              <Box sx={tagsRowSx}>
                <Chip
                  label={getNotificationProcessingTypeLabel(job.processingType)}
                  size="small"
                  sx={processingTypeChipSx}
                />
              </Box>
            )}
            {resultUrl && (
              <Typography sx={viewResultsLinkSx}>
                {NOTIFICATION_COPY.actions.viewResults}
              </Typography>
            )}
          </>
        )}
      </Box>

      <IconButton
        type="button"
        size="small"
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          handleClose();
        }}
        sx={{ p: 0.25, mt: 0.125 }}>
        <CloseIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
      </IconButton>
    </Box>
  );

  if (variant === PROCESSING) {
    return (
      <Box
        role="presentation"
        sx={{
          ...toastProcessingSx,
          width: 360,
          boxShadow: '0px 8px 32px rgba(0,0,0,0.13)',
          ...slideAnimation,
          cursor: 'default',
        }}>
        <Box sx={toastInnerSx}>{inner}</Box>
      </Box>
    );
  }

  const content = (
    <Box sx={{ ...outerSx, p: 0, width: 360, backgroundColor: '#fff', boxShadow: '0px 8px 32px rgba(0,0,0,0.13)', ...slideAnimation }}>
      <Box sx={{ position: 'relative', zIndex: 1, p: 1.5 }}>{inner}</Box>
    </Box>
  );

  if (resultUrl) {
    return (
      <Link
        to={resultUrl}
        onClick={() => {
          handleClose();
        }}
        style={{ textDecoration: 'none', display: 'block', cursor: 'pointer' }}>
        {content}
      </Link>
    );
  }

  return <Box sx={{ cursor: 'default' }}>{content}</Box>;
};

export default NotificationToast;
